import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, TrendingDown, AlertTriangle, Lightbulb, Target, ShoppingCart, Wallet, Sparkles, ArrowUpRight, ArrowDownRight, BookOpen, Check, Clock } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts'
import { api } from '../services/api'
import { formatCurrency, getCurrentMonth, getMonthLabel, getPreviousMonth, getNextMonth, getPercentage } from '../utils/format'

export default function Dashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [month, setMonth] = useState(getCurrentMonth())

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await api.getDashboard(month)
      setData(result)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }, [month])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const onEvents = (e) => {
      const evs = e.detail || []
      if (evs.some(ev => ['budget','expense','grocery','payment','project','signer'].includes(ev.entity))) {
        fetchData()
      }
    }
    window.addEventListener('fb:events', onEvents)
    return () => window.removeEventListener('fb:events', onEvents)
  }, [fetchData])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-primary-700 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-8">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchData} className="btn-primary mt-4">Réessayer</button>
      </div>
    )
  }

  if (!data) return null

  const { advisor, cahierStatus } = data

  const pieData = data.categoriesData
    .filter(c => c.spent > 0)
    .map(c => ({ name: c.category_name, value: c.spent, color: c.category_color }))

  const trendData = data.trend.map(t => ({
    month: t.month.split('-')[1],
    label: getMonthLabel(t.month).split(' ')[0].slice(0, 3),
    total: t.total,
  }))

  const remaining = data.remaining
  const isOverBudget = remaining < 0
  const realEpargne = data.realEpargne
  const isNegativeEpargne = realEpargne !== null && realEpargne < 0

  return (
    <div className="space-y-4">
      {/* Month selector */}
      <div className="flex items-center justify-between">
        <button onClick={() => setMonth(getPreviousMonth(month))} className="p-2 rounded-lg active:bg-slate-100">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-semibold capitalize">{getMonthLabel(month)}</h2>
        <button
          onClick={() => setMonth(getNextMonth(month))}
          disabled={month >= getCurrentMonth()}
          className="p-2 rounded-lg active:bg-slate-100 disabled:opacity-30"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Flux financier : revenu → dépenses → épargne */}
      {data.monthlyIncome > 0 ? (
        <div className="card bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-slate-500 flex items-center gap-1.5"><Wallet size={14} /> Revenus du mois</span>
            <span className="text-lg font-bold text-primary-700">{formatCurrency(data.monthlyIncome)}</span>
          </div>
          <div className="h-2 bg-white rounded-full overflow-hidden mb-1.5">
            <div
              className={`h-full rounded-full transition-all ${isNegativeEpargne ? 'bg-red-500' : 'bg-primary-600'}`}
              style={{ width: `${Math.min((data.totalExpenses / data.monthlyIncome) * 100, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Dépenses : <span className="font-semibold text-slate-700">{formatCurrency(data.totalExpenses)}</span></span>
            <span className={`font-bold ${isNegativeEpargne ? 'text-red-500' : 'text-green-600'}`}>
              {isNegativeEpargne ? '⚠️' : ''}  Épargne : {formatCurrency(Math.abs(realEpargne))}
            </span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="card">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Wallet size={16} /> Budget</div>
            <p className="text-xl font-bold">{formatCurrency(data.totalBudget)}</p>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><TrendingDown size={16} /> Dépensé</div>
            <p className="text-xl font-bold text-red-500">{formatCurrency(data.totalExpenses)}</p>
          </div>
          <div className={`card ${isOverBudget ? 'bg-red-50 border-red-200' : 'bg-primary-50 border-primary-200'}`}>
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
              {isOverBudget ? <AlertTriangle size={16} className="text-red-500" /> : <Sparkles size={16} className="text-primary-600" />} Reste
            </div>
            <p className={`text-xl font-bold ${isOverBudget ? 'text-red-500' : 'text-primary-700'}`}>{formatCurrency(Math.abs(remaining))}</p>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Target size={16} /> Épargne visée</div>
            <p className="text-xl font-bold text-primary-700">{formatCurrency(data.savingsTarget)}</p>
          </div>
        </div>
      )}

      {/* Statut Cahier du mois */}
      {cahierStatus && (
        <div className="card">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3 text-slate-700">
            <BookOpen size={16} /> Cahier — État des paiements
          </h3>
          <div className="space-y-2">
            {/* Loyer : mensuel, double sig */}
            {(() => {
              const r = cahierStatus.loyer?.rec
              const paid = r?.sig_count >= 2
              const partial = r && r.sig_count === 1
              return (
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${paid ? 'bg-green-100 text-green-600' : partial ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-500'}`}>
                    {paid ? <Check size={12} /> : partial ? '1' : '!'}
                  </span>
                  <span className="flex-1 text-sm">Loyer</span>
                  <span className={`text-xs ${paid ? 'text-green-600' : partial ? 'text-amber-600' : 'text-red-500'}`}>
                    {paid ? '2/2 signé' : partial ? '1/2 signé' : 'Non signé'}
                  </span>
                </div>
              )
            })()}
            {/* EW : bimensuel mois pairs */}
            {cahierStatus.ew?.due && (() => {
              const r = cahierStatus.ew?.rec
              const paid = r?.sig_count >= 2
              const partial = r && r.sig_count === 1
              return (
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${paid ? 'bg-green-100 text-green-600' : partial ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-500'}`}>
                    {paid ? <Check size={12} /> : partial ? '1' : '!'}
                  </span>
                  <span className="flex-1 text-sm">Électricité / Eau</span>
                  <span className={`text-xs ${paid ? 'text-green-600' : partial ? 'text-amber-600' : 'text-red-500'}`}>
                    {paid ? '2/2 signé' : partial ? '1/2 signé' : 'Non signé'}
                  </span>
                </div>
              )
            })()}
            {!cahierStatus.ew?.due && (
              <div className="flex items-center gap-2 opacity-40">
                <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Clock size={10} /></span>
                <span className="flex-1 text-sm">Électricité / Eau</span>
                <span className="text-xs text-slate-400">Non dû (mois impair)</span>
              </div>
            )}
            {/* WiFi : mensuel, simple */}
            {(() => {
              const paid = !!cahierStatus.wifi?.rec
              return (
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${paid ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-500'}`}>
                    {paid ? <Check size={12} /> : <Clock size={10} />}
                  </span>
                  <span className="flex-1 text-sm">WiFi</span>
                  <span className={`text-xs ${paid ? 'text-green-600' : 'text-amber-500'}`}>{paid ? 'Confirmé' : 'En attente'}</span>
                </div>
              )
            })()}
            {/* Gaz : bimensuel mois pairs */}
            {cahierStatus.gaz?.due && (() => {
              const paid = !!cahierStatus.gaz?.rec
              return (
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${paid ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-500'}`}>
                    {paid ? <Check size={12} /> : <Clock size={10} />}
                  </span>
                  <span className="flex-1 text-sm">Gaz</span>
                  <span className={`text-xs ${paid ? 'text-green-600' : 'text-amber-500'}`}>{paid ? 'Confirmé' : 'En attente'}</span>
                </div>
              )
            })()}
            {!cahierStatus.gaz?.due && (
              <div className="flex items-center gap-2 opacity-40">
                <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Clock size={10} /></span>
                <span className="flex-1 text-sm">Gaz</span>
                <span className="text-xs text-slate-400">Non dû (mois impair)</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Month comparison */}
      {data.prevTotalExpenses > 0 && (
        <div className="card flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">vs mois précédent</p>
            <p className="font-semibold">{formatCurrency(data.prevTotalExpenses)}</p>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
            data.expenseChange > 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
          }`}>
            {data.expenseChange > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
            {Math.abs(data.expenseChange).toFixed(0)}%
          </div>
        </div>
      )}

      {/* Advisor Section */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Sparkles size={20} className="text-primary-600" /> Conseiller
        </h3>

        {/* Alerts */}
        {advisor.alerts.length > 0 && (
          <div className="space-y-2">
            {advisor.alerts.map((alert, i) => (
              <div key={i} className={`rounded-2xl p-4 border ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                alert.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className={
                    alert.severity === 'critical' ? 'text-red-500' :
                    alert.severity === 'high' ? 'text-orange-500' : 'text-amber-500'
                  } />
                  <div>
                    <p className="font-semibold text-sm">{alert.title}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{alert.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Insights */}
        {advisor.insights.length > 0 && (
          <div className="space-y-2">
            {advisor.insights.map((insight, i) => (
              <div key={i} className="card bg-primary-50 border-primary-200">
                <div className="flex items-start gap-3">
                  <Lightbulb size={20} className="text-primary-600" />
                  <div>
                    <p className="font-semibold text-sm">{insight.title}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{insight.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Recommendations */}
        {advisor.recommendations.length > 0 && (
          <div className="space-y-2">
            {advisor.recommendations.map((rec, i) => (
              <div key={i} className="card">
                <div className="flex items-start gap-3">
                  <span className="text-xl">{rec.icon || '💡'}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{rec.title}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{rec.message}</p>
                    {rec.potential_savings > 0 && (
                      <p className="text-sm text-primary-600 font-medium mt-1">
                        Épargne possible: {formatCurrency(rec.potential_savings)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total potential savings */}
        {advisor.summary.total_potential_savings > 0 && (
          <div className="card bg-gradient-to-br from-primary-600 to-primary-800 text-white text-center">
            <p className="text-sm opacity-90">Épargne potentielle totale</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(advisor.summary.total_potential_savings)}</p>
            <p className="text-xs opacity-75 mt-1">En suivant les recommandations du conseiller</p>
          </div>
        )}

        {/* No alerts */}
        {advisor.alerts.length === 0 && advisor.insights.length === 0 && advisor.recommendations.length === 0 && (
          <div className="card text-center py-6">
            <span className="text-3xl">✅</span>
            <p className="text-sm text-slate-600 mt-2">Tout est sous contrôle ce mois-ci !</p>
          </div>
        )}
      </div>

      {/* Pie chart - expenses by category */}
      {pieData.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Répartition des dépenses</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {pieData.sort((a, b) => b.value - a.value).slice(0, 5).map((item, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                  <span className="text-slate-600">{item.name}</span>
                </div>
                <span className="font-medium">{formatCurrency(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trend chart */}
      {trendData.length > 1 && (
        <div className="card">
          <h3 className="font-semibold mb-3">Tendance (6 derniers mois)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trendData}>
              <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="total" fill="#0f766e" radius={[8, 8, 0, 0]} />
              {data.totalBudget > 0 && (
                <ReferenceLine y={data.totalBudget} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Budget', fontSize: 10, fill: '#ef4444' }} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Category breakdown */}
      <div className="card">
        <h3 className="font-semibold mb-3">Détail par catégorie</h3>
        <div className="space-y-3">
          {data.categoriesData
            .filter(c => c.budgeted > 0 || c.spent > 0)
            .sort((a, b) => b.spent - a.spent)
            .map((cat, i) => {
              const percent = getPercentage(cat.spent, cat.budgeted)
              const isOver = cat.spent > cat.budgeted && cat.budgeted > 0
              return (
                <div key={i}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-1.5">
                      <span>{cat.category_icon}</span>
                      <span className="font-medium">{cat.category_name}</span>
                      {!cat.is_essential && <span className="badge bg-slate-100 text-slate-500 text-[10px]">Non essentiel</span>}
                    </span>
                    <span className={isOver ? 'text-red-500 font-medium' : 'text-slate-600'}>
                      {formatCurrency(cat.spent)} / {formatCurrency(cat.budgeted)}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${isOver ? 'bg-red-500' : 'bg-primary-500'}`}
                      style={{ width: `${Math.min(percent, 100)}%`, background: isOver ? '#ef4444' : cat.category_color }}
                    />
                  </div>
                </div>
              )
            })}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card text-center">
          <ShoppingCart size={24} className="mx-auto text-slate-400 mb-1" />
          <p className="text-2xl font-bold">{data.groceries.pendingCount}</p>
          <p className="text-xs text-slate-500">Articles en attente</p>
          {data.groceries.estimatedTotal > 0 && (
            <p className="text-xs text-slate-400 mt-1">~{formatCurrency(data.groceries.estimatedTotal)}</p>
          )}
        </div>
        <div className="card text-center">
          <Target size={24} className="mx-auto text-slate-400 mb-1" />
          <p className="text-2xl font-bold">{data.projects.length}</p>
          <p className="text-xs text-slate-500">Projets actifs</p>
        </div>
      </div>
    </div>
  )
}
